import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Calendar } from 'primereact/calendar'
import FormField from '../FormField'
import { resolveFieldError } from '../../../lib/forms/messages'

/** 24 saat biçimi "HH:mm" (00:00–23:59). */
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/

/** "HH:mm" → Date (bugünün tarihinde o saat/dakika); boş/geçersiz → null. */
function parseTime(value: string | null | undefined): Date | null {
  if (!value || !TIME_RE.test(value)) return null
  const [h, m] = value.split(':').map(Number)
  const d = new Date()
  d.setHours(h, m, 0, 0)
  return d
}

/** Date → "HH:mm" (24 saat, sıfır dolgulu); null → "". */
function formatTime(date: Date | null): string {
  if (!date) return ''
  const h = String(date.getHours()).padStart(2, '0')
  const m = String(date.getMinutes()).padStart(2, '0')
  return `${h}:${m}`
}

/**
 * RHF-bağlı saat alanı — PrimeReact `Calendar` **timeOnly** (24 saat) + `FormField`. Saat girişleri için tek
 * kaynak (Reuse Mandate; `FormDateField` ikizi). Form değeri "HH:mm" string tutulur (şema `TIME_RE` + tarih+saat
 * birleştirmesiyle uyumlu); Calendar'a Date olarak verilir, geri string'e çevrilir.
 */
interface FormTimeFieldProps<T extends FieldValues> {
  control: Control<T>
  name: FieldPath<T>
  label: string
  required?: boolean
  full?: boolean
  /**
   * Panel **açıldığında** alan boşsa yazılacak başlangıç değeri ("HH:mm" döndüren bir fonksiyon).
   *
   * <p><b>Neden gerekli:</b> PrimeReact `timeOnly` paneli açıldığında bir saat **gösterir** ama onu
   * forma <b>yazmaz</b> — kullanıcı ok tuşlarıyla değeri değiştirene kadar alan boş kalır. Yani
   * "gördüğüm saat doğru, onaylıyorum" demenin bir yolu yoktur; panelin "Tamam" düğmesi de yoktur.
   * Bu geri çağrı verildiğinde alan açılır açılmaz dolar ve kullanıcı yalnız değiştirmek isterse
   * dokunur. Kardeş bileşen {@code FormTimeRangeField} <b>aynı amacı</b> içeride zaten uyguluyor —
   * ama <b>aynı davranışı değil</b>: oradaki bitiş varsayılanı gün taşmasına izin verir (23:30 →
   * ertesi gün 00:30), buradaki geri çağrıyı yazan ise kendi kenar durumunu seçer. Bu yüzden mantık
   * ortaklaştırılmadı ve prop <b>opsiyoneldir</b>: her saat alanı kendiliğinden dolmak istemeyebilir.
   */
  fillOnOpen?: () => string
}

export default function FormTimeField<T extends FieldValues>({
  control,
  name,
  label,
  required,
  full,
  fillOnOpen,
}: Readonly<FormTimeFieldProps<T>>) {
  const { t } = useTranslation()
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormField
          label={label}
          htmlFor={name}
          required={required}
          full={full}
          error={resolveFieldError(fieldState.error?.message, t)}
        >
          <Calendar
            inputId={name}
            value={parseTime(field.value)}
            onChange={(e) => field.onChange(formatTime((e.value as Date | null) ?? null))}
            onShow={() => {
              if (fillOnOpen && !field.value) field.onChange(fillOnOpen())
            }}
            onBlur={field.onBlur}
            timeOnly
            hourFormat="24"
            showIcon
            icon="pi pi-clock"
            className={fieldState.error ? 'p-invalid' : undefined}
          />
        </FormField>
      )}
    />
  )
}
